import React, { useMemo, useCallback } from "react";
import ArticleSearchContainer from "../../../containers/ArticleSearchContainer";
import usePortalSearchItems from "../common/usePortalSearchItems";
import usePortalDialog from "../common/usePortalDialog";
import PortalPCAreaCheckboxList from "./PortalPCAreaCheckboxList";
import PortalPCWardCheckboxList from "./PortalPCWardCheckboxList";
import PortalPCCheckboxList from "./PortalPCCheckboxList";
import PortalPCRangeField from "./PortalPCRangeField";
import PortalPCPriceField from "./PortalPCPriceField";
import PortalPCDateField from "./PortalPCDateField";

type Props = {
  onClose: () => void;
};

const TITLE_ID = "portal-pc-detail-modal-title";

function PortalPCDetailSearchModal({ onClose }: Props) {
  const dialogRef = usePortalDialog({ onClose });
  const { word, setWord, aggsTotal, search, searchDeps } =
    ArticleSearchContainer.useContainer();
  const {
    kindItem,
    genreItem,
    positionItem,
    characteristicItem,
    atmosphereItem,
    skillItem,
    termItem,
    priceItem,
    dateItem,
  } = usePortalSearchItems();

  const onSearch = useCallback(() => {
    search();
    onClose();
  }, [...searchDeps, onClose]);

  const checkboxItems = useMemo(
    () =>
      [
        kindItem,
        genreItem,
        positionItem,
        characteristicItem,
        atmosphereItem,
        skillItem,
      ].filter(Boolean),
    [
      kindItem,
      genreItem,
      positionItem,
      characteristicItem,
      atmosphereItem,
      skillItem,
    ]
  );

  const onChangeWord = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setWord(e.target.value),
    [setWord]
  );

  const onClickOverlay = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  return (
    <div
      className="portalPcDetailModalOverlay"
      role="presentation"
      onClick={onClickOverlay}
    >
      <div
        ref={dialogRef}
        className="portalPcDetailModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
      >
        <div className="portalPcDetailModalHeader">
          <h2 className="portalPcDetailModalTitle" id={TITLE_ID}>
            条件を指定して探す
          </h2>
          <p className="portalPcDetailModalTotal">
            該当ボランティア <strong>{aggsTotal}</strong> 件
          </p>
          <button
            type="button"
            className="portalPcDetailModalClose"
            aria-label="閉じる"
            onClick={onClose}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>

        <div className="portalPcDetailModalBody">
          <label
            className="portalPcDetailModalKeyword"
            htmlFor="portal-pc-detail-keyword"
          >
            <span className="portalPcDetailModalKeywordLabel">
              キーワードで探す
            </span>
            <span className="portalPcDetailModalKeywordInput">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                id="portal-pc-detail-keyword"
                type="text"
                placeholder="活動名・団体名などを入力"
                value={word}
                onChange={onChangeWord}
              />
            </span>
          </label>

          <div className="portalPcDetailModalTable">
            <div className="portalPcDetailModalSection">
              <div className="portalPcDetailModalSectionLabel">活動場所</div>
              <div className="portalPcDetailModalSectionBody">
                <div className="portalPcDetailModalOnlineRow">
                  <PortalPCAreaCheckboxList idPrefix="portal-pc-modal" />
                </div>
                <PortalPCWardCheckboxList />
              </div>
            </div>

            {checkboxItems.map((item) => (
              <div className="portalPcDetailModalSection" key={item.name}>
                <div className="portalPcDetailModalSectionLabel">
                  {item.title}
                </div>
                <div className="portalPcDetailModalSectionBody">
                  <PortalPCCheckboxList
                    idPrefix="portal-pc-modal"
                    item={item}
                  />
                </div>
              </div>
            ))}

            {termItem && (
              <div className="portalPcDetailModalSection">
                <div className="portalPcDetailModalSectionLabel">期間</div>
                <div className="portalPcDetailModalSectionBody">
                  <PortalPCRangeField item={termItem} />
                </div>
              </div>
            )}
            {priceItem && (
              <div className="portalPcDetailModalSection">
                <div className="portalPcDetailModalSectionLabel">費用</div>
                <div className="portalPcDetailModalSectionBody">
                  <PortalPCPriceField item={priceItem} />
                </div>
              </div>
            )}
            {dateItem && (
              <div className="portalPcDetailModalSection">
                <div className="portalPcDetailModalSectionLabel">活動日程</div>
                <div className="portalPcDetailModalSectionBody">
                  <PortalPCDateField item={dateItem} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="portalPcDetailModalFooter">
          <button
            type="button"
            className="p_btnLarge isMain"
            onClick={onSearch}
          >
            <i className="fa fa-search" aria-hidden="true" />
            検索する（{aggsTotal}件）
          </button>
        </div>
      </div>
    </div>
  );
}

export default PortalPCDetailSearchModal;
